// Shared payload schema for stored / shared measurements. This is the exact
// JSON shape written to POST /api/results and read back by GET /api/results/{id}
// and by the Share page, and it is also what History stores locally.

import type { NodeInfo, RunResult, SweepResult, Verdict } from '@/engine/types';

/** Schema version for stored payloads. Bump on any breaking shape change. */
export const SHARE_SCHEMA = 1 as const;

/** A run-type payload: one or more sequential per-node runs plus a verdict. */
export interface RunPayload {
  schema: typeof SHARE_SCHEMA;
  kind: 'run';
  createdAt: number;
  title: string;
  nodes: NodeInfo[];
  runs: RunResult[];
  verdict: Verdict | null;
}

/** A sweep-type payload. */
export interface SweepPayload {
  schema: typeof SHARE_SCHEMA;
  kind: 'sweep';
  createdAt: number;
  title: string;
  nodes: NodeInfo[];
  sweep: SweepResult;
}

/** Any stored measurement payload. */
export type SharePayload = RunPayload | SweepPayload;

/** Build a run payload from finished runs. */
export function makeRunPayload(
  runs: RunResult[],
  nodes: NodeInfo[],
  verdict: Verdict | null,
  title?: string,
): RunPayload {
  return {
    schema: SHARE_SCHEMA,
    kind: 'run',
    createdAt: Date.now(),
    title: title ?? defaultRunTitle(runs, nodes),
    nodes,
    runs,
    verdict,
  };
}

/** Build a sweep payload from a finished sweep. */
export function makeSweepPayload(
  sweep: SweepResult,
  nodes: NodeInfo[],
  title?: string,
): SweepPayload {
  return {
    schema: SHARE_SCHEMA,
    kind: 'sweep',
    createdAt: Date.now(),
    title: title ?? `Sweep · ${sweep.cells.length} cells`,
    nodes,
    sweep,
  };
}

/** Validate an unknown blob into a SharePayload (throws on mismatch). */
export function parseSharePayload(input: unknown): SharePayload {
  const p = input as Partial<SharePayload> | null;
  if (!p || typeof p !== 'object') throw new Error('malformed payload');
  if (p.schema !== SHARE_SCHEMA) {
    throw new Error(`unsupported payload schema: ${String(p.schema)}`);
  }
  if (p.kind === 'run' && Array.isArray((p as RunPayload).runs)) {
    return p as RunPayload;
  }
  if (p.kind === 'sweep' && (p as SweepPayload).sweep) {
    return p as SweepPayload;
  }
  throw new Error('payload is neither a run nor a sweep');
}

function defaultRunTitle(runs: RunResult[], nodes: NodeInfo[]): string {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const names = runs.map((r) => byId.get(r.nodeId)?.name ?? r.tier);
  if (names.length === 1) return names[0];
  return `${names.length} nodes · ${names.join(', ')}`;
}
